import React from 'react';

import { Typography } from '@mui/material';
import { isEmpty } from 'es-toolkit/compat';
import { DateTime } from 'luxon';
import { useForm } from 'react-final-form';
import { useDebounce } from 'use-debounce';

import { Field, FormSpy } from '@hrlogics/react-components/form';

import { useGetEmployersListForVerificationRequestByEmployeeDataQuery } from 'src/api';
import { SelectField } from 'src/components/finalForm';
import { VerificationRequestCreateFormFields } from 'src/constants/verificationRequest';
import { useVerificationRequestFormData } from 'src/hooks/verificationRequest';
import { buildEmployersListForVerificationRequest } from 'src/utils/employer';

import { VerificationRequestCreateFormValues } from './types';

type VerificationRequestCreateModalFormEmployerFieldProps = {
  showNonLiveEmployers?: boolean;
};

export const VerificationRequestCreateModalFormEmployerField: React.FC<
  VerificationRequestCreateModalFormEmployerFieldProps
> = ({ showNonLiveEmployers }) => {
  const { getState, change } = useForm<VerificationRequestCreateFormValues>();
  const { values, errors = {} } = getState();

  const { isEmployeeExists, setIsEmployersInfoLoading, setIsEmployersHavePayrollsInfo } =
    useVerificationRequestFormData();

  const isFieldsValid = React.useMemo(() => {
    return !(VerificationRequestCreateFormFields.ssn in errors);
  }, [errors]);

  const [{ ssn, employeeDateOfBirth }] = useDebounce<Partial<VerificationRequestCreateFormValues>>(
    values,
    DEBOUNCE_DELAY,
  );

  const employeeDateOfBirthDateTime = React.useMemo(() => {
    return employeeDateOfBirth ? DateTime.fromISO(employeeDateOfBirth) : undefined;
  }, [employeeDateOfBirth]);

  const { data, loading } = useGetEmployersListForVerificationRequestByEmployeeDataQuery({
    variables: {
      employeeSsn: ssn ?? '',
      employeeBirthDate: employeeDateOfBirthDateTime?.isValid
        ? employeeDateOfBirthDateTime.toISODate()
        : '',
      showInactiveEmployers: showNonLiveEmployers,
    },
    skip: !isFieldsValid || !ssn || !employeeDateOfBirthDateTime?.isValid || !isEmployeeExists,
  });

  const employersSelectOptions = React.useMemo(
    () =>
      buildEmployersListForVerificationRequest(
        data?.getEmployersListForVerificationRequestByEmployeeData.employers,
      ),
    [data?.getEmployersListForVerificationRequestByEmployeeData.employers],
  );

  const isThereInactiveEmployers = React.useMemo(
    () =>
      data?.getEmployersListForVerificationRequestByEmployeeData.isThereInactiveEmployers ?? false,
    [data?.getEmployersListForVerificationRequestByEmployeeData.isThereInactiveEmployers],
  );

  const setIsEmployersInfoLoadingEffectEvent = React.useEffectEvent((loading: boolean) => {
    setIsEmployersInfoLoading(loading);
  });
  React.useEffect(() => {
    setIsEmployersInfoLoadingEffectEvent(loading);
  }, [loading]);

  const isEmployersFound = React.useMemo(() => {
    return (
      !loading &&
      employersSelectOptions.length > 0 &&
      !(VerificationRequestCreateFormFields.ssn in errors)
    );
  }, [loading, employersSelectOptions.length, errors]);

  const resetEmployersFieldEffectEvent = React.useEffectEvent(() => {
    change(VerificationRequestCreateFormFields.employers, []);
  });
  React.useEffect(() => {
    if (!isEmployersFound) {
      resetEmployersFieldEffectEvent();
    }
  }, [isEmployersFound]);

  const showLiveEmployersAttention = React.useMemo(() => {
    return (
      !loading &&
      ssn &&
      !(VerificationRequestCreateFormFields.ssn in errors) &&
      isThereInactiveEmployers
    );
  }, [loading, ssn, errors, isThereInactiveEmployers]);

  React.useEffect(() => {
    if (isEmployeeExists && !loading && employersSelectOptions.length === 0) {
      change(
        VerificationRequestCreateFormFields.error,
        'There is no employment data for this Employee on the platform',
      );
    } else {
      change(VerificationRequestCreateFormFields.error, null);
    }
  }, [change, employersSelectOptions.length, isEmployeeExists, loading]);

  const handleFormValuesChange = React.useCallback(
    (values: VerificationRequestCreateFormValues) => {
      if (isEmpty(values.employers)) {
        setIsEmployersHavePayrollsInfo(false);
        return;
      }

      const isEmployersHavePayrollsInfo =
        data?.getEmployersListForVerificationRequestByEmployeeData.employers.some(
          employer =>
            values.employers.some(selectedEmployer => selectedEmployer.id === employer.id) &&
            employer.isPayrollsAvailable,
        ) ?? false;

      setIsEmployersHavePayrollsInfo(isEmployersHavePayrollsInfo);
    },
    [
      data?.getEmployersListForVerificationRequestByEmployeeData.employers,
      setIsEmployersHavePayrollsInfo,
    ],
  );

  return (
    <React.Fragment>
      <Field
        name={VerificationRequestCreateFormFields.employers}
        component={SelectField}
        label="Employers"
        multiple
        required
        options={employersSelectOptions}
        fullWidth
        disabled={!isEmployeeExists || !isEmployersFound}
        scrollableContent
        demoPrivacy
      />

      <FormSpy<VerificationRequestCreateFormValues>
        subscription={{ values: true }}
        onChange={({ values }) => handleFormValuesChange(values)}
      />

      {showLiveEmployersAttention && (
        <Typography
          whiteSpace="pre-line"
          variant="body3"
          sx={theme => ({ color: theme.palette.error.main, paddingX: theme.spacing(2) })}
        >
          There's some employee data from employers that aren't active on the platform yet
        </Typography>
      )}
    </React.Fragment>
  );
};

const DEBOUNCE_DELAY = 500;
